<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  transaction: { type: Object, required: true },
  busyAction: { type: String, default: '' },
});

const emit = defineEmits([
  'archive',
  'details',
  'deliver',
  'finalize',
  'register',
  'restore',
  'review',
  'signatures',
]);
const { t } = useI18n();

const permissions = computed(() => props.transaction.permissions || {});
const isDraft = computed(() => props.transaction.sales_stage === 'rascunho');
const isReview = computed(() => props.transaction.sales_stage === 'revisao');
const isArchived = computed(
  () => props.transaction.sales_stage === 'arquivado'
);
const isDelivered = computed(
  () => props.transaction.sales_stage === 'entregue'
);
const isComplete = computed(
  () => props.transaction.progress?.complete === true
);
const signatureTracking = computed(
  () => props.transaction.signature_tracking || {}
);
const isCollectingSignatures = computed(
  () =>
    isDraft.value &&
    ['sent', 'partially_signed'].includes(signatureTracking.value.status) &&
    Number(signatureTracking.value.pending_count) > 0
);
const canEditDraft = computed(
  () =>
    isDraft.value &&
    !isCollectingSignatures.value &&
    permissions.value.can_save_draft
);
const canDeliver = computed(
  () =>
    isDraft.value &&
    !isCollectingSignatures.value &&
    permissions.value.can_deliver
);
const canCorrect = computed(
  () => isReview.value && permissions.value.can_resubmit_review
);
const canFinalize = computed(
  () => isDelivered.value && permissions.value.can_finalize
);
</script>

<template>
  <div
    class="flex flex-wrap items-center justify-end gap-2"
    :aria-label="t('SALES.ARIA.ACTIONS')"
  >
    <LaisPrimaryButton
      v-if="isCollectingSignatures"
      size="sm"
      icon="i-lucide-file-signature"
      @click="emit('signatures')"
    >
      {{ t('SALES.ACTIONS.TRACK_SIGNATURES') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-else
      variant="ghost"
      size="sm"
      icon="i-lucide-info"
      @click="emit('details')"
    >
      {{ t('SALES.ACTIONS.DETAILS') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="canEditDraft"
      variant="ghost"
      size="sm"
      icon="i-lucide-pencil"
      @click="emit('register')"
    >
      {{ t('SALES.ACTIONS.EDIT') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="canDeliver"
      size="sm"
      icon="i-lucide-check"
      :disabled="!isComplete"
      @click="emit('deliver')"
    >
      {{ t('SALES.ACTIONS.DELIVER') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="canCorrect"
      size="sm"
      icon="i-lucide-pencil-line"
      @click="emit('review')"
    >
      {{ t('SALES.ACTIONS.CORRECT') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="canFinalize"
      size="sm"
      icon="i-lucide-badge-check"
      :is-loading="busyAction === 'finalize'"
      @click="emit('finalize')"
    >
      {{ t('SALES.ACTIONS.FINALIZE') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="isArchived && permissions.can_restore"
      variant="ghost"
      size="sm"
      icon="i-lucide-rotate-ccw"
      :is-loading="busyAction === 'restore'"
      @click="emit('restore')"
    >
      {{ t('SALES.ACTIONS.RESTORE') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="isDraft && !isCollectingSignatures && permissions.can_archive"
      variant="ghost"
      size="sm"
      icon="i-lucide-archive"
      :is-loading="busyAction === 'archive'"
      @click="emit('archive')"
    >
      {{ t('SALES.ACTIONS.ARCHIVE') }}
    </LaisPrimaryButton>
  </div>
</template>
